import {
    View,
    Heading,
    Text,
    colors,
    GridColumn,
    Link,
} from '@fairygodboss/ui';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import React, { forwardRef, useRef, useState } from 'react';

import { getCloudinaryInfo } from '../../components/Images/ResponsiveImage';
import { Author } from '../../components/SharedLayout/Author/Author';
import { IMAGE_CDN_URL } from '../../utils/imageCDN';
import useUser from '../../utils/useUser';

const Convincer = dynamic(
    () => import('./Convincer').then((mod) => mod.Convincer),
    {
        loading: () => <></>,
        ssr: false,
    }
);

export const ContentInfo = forwardRef(
    (
        {
            contentImage,
            contentTitle,
            author,
            contentModifiedDate,
            createdDate,
            paidCompany,
            isSponsor,
            showSponsoredBy,
            topConvincerRef,
            description,
            hash,
            type,
        },
        ref
    ) => {
        const authorPaddingTop = useState(28);
        const { alt, credit, src } = contentImage;
        const { user } = useUser();
        const authorRef = useRef();
        const hero = getCloudinaryInfo({ cloudinaryID: src });
        return (
            <View display="flex" justifyContent="space-between">
                <style global jsx>
                    {`
                        .paidCompany {
                            text-decoration: none;
                        }
                        .paidCompany:hover {
                            color: ${colors.primary[60]} !important;
                        }
                        .paidCompany:visited {
                            color: ${colors.primary[60]} !important;
                        }
                    `}
                </style>
                <GridColumn
                    size={{
                        xs: 12,
                        sm: 12,
                        md: 7.8,
                        lg: 7.6,
                    }}
                    pl="0px"
                    pr="0px"
                >
                    {description?.length ? (
                        <Heading
                            pt="24px"
                            variant="headingSm"
                            fontWeight="600"
                            fontSize={{ _: '16px', md: '20px' }}
                            lineHeight={{ _: '24px', md: '30px' }}
                            color={colors.neutral[100]}
                            as="h2"
                        >
                            {description}
                        </Heading>
                    ) : null}
                    {!!(isSponsor && paidCompany?.length > 0 && showSponsoredBy) && (
                        <Text
                            pt="24px"
                            as="p"
                            margin="0"
                            textAlign="left"
                            variant="labelLg"
                            color={colors.neutral[60]}
                        >
                            Sponsored by
                            {' '}
                            <Link
                                href={`/company-overview/${paidCompany[0].url}`}
                                variant="inline"
                                target="_self"
                                className="paidCompany"
                            >
                                {paidCompany[0].longName}
                            </Link>
                        </Text>
                    )}
                    <View
                        ref={ref}
                        pt={{ _: '24px', sm: '32px', md: '48px' }}
                        css={{
                            '.component-Elements-Picture': {
                                display: 'block !important',
                                height: 'auto !important',
                            },
                            img: {
                                display: 'block',
                                height: 'auto',
                                width: '100%',
                            },
                        }}
                    >
                        <Image
                            className="component-Elements-Picture__img"
                            src={`${IMAGE_CDN_URL}/dpr_1,q_auto,c_scale,f_webp,w_669${hero.filename}?temp=1`}
                            sizes="(max-width: 480px) 96vw, (max-width: 768px) 74vw,(max-width: 1024px) 64vw. 56vw"
                            fetchpriority="high"
                            alt={alt || 'profile image'}
                            width="669"
                            height="376"
                            priority={true}
                            loading="eager"
                            style={{
                                maxWidth: '100%',
                                height: 'auto',
                                aspectRatio: '669 / 376',
                            }}
                        />
                    </View>
                    <Text
                        as="p"
                        color={colors.neutral[60]}
                        margin="0"
                        textAlign="left"
                        variant="labelSm"
                        pt="4px"
                    >
                        {credit}
                    </Text>
                    {author && (
                        <Author
                            pt={`${authorPaddingTop}px`}
                            author={author}
                            type={type}
                            hash={hash}
                            contentTitle={contentTitle}
                            contentCreatedDate={createdDate}
                            contentModifiedDate={contentModifiedDate}
                            ref={authorRef}
                        />
                    )}
                </GridColumn>
                {!user?.isLoggedIn ? (
                    <GridColumn
                        size={{
                            xs: 0,
                            sm: 0,
                            md: 3.5,
                            lg: 3.05,
                        }}
                        pr={{ _: '0px', md: '24px', lg: '0px' }}
                        pl="0px"
                    >
                        <Convincer onConvincerClick ref={topConvincerRef} />
                    </GridColumn>
                ) : (
                    <></>
                )}
            </View>
        );
    }
);

ContentInfo.displayName = 'ContentInfo';
